import { useState, useEffect } from 'react'
import { usePlayground } from '../store'
import { MobileSheet } from './MobileSheet'
import { Icons } from './TabToolbar'

export interface StructureFilters {
    subsystems: boolean
    pipelines: boolean
    cycles: boolean
    forks: boolean
    joins: boolean
    hubs: boolean
    deadlock: boolean
    overflow: boolean
}

const FILTER_KEYS: (keyof StructureFilters)[] = ['subsystems', 'pipelines', 'cycles', 'forks', 'joins', 'hubs', 'deadlock', 'overflow']
const ALL_OFF: StructureFilters = { subsystems: false, pipelines: false, cycles: false, forks: false, joins: false, hubs: false, deadlock: false, overflow: false }
const ALL_ON: StructureFilters = { subsystems: true, pipelines: true, cycles: true, forks: true, joins: true, hubs: true, deadlock: true, overflow: true }

interface CanvasSettingsProps {
    open: boolean
    onClose: () => void
    filters: StructureFilters
    onFiltersChange: (f: StructureFilters) => void
}

export function CanvasSettings({ open, onClose, filters, onFiltersChange }: CanvasSettingsProps) {
    const groupBy = usePlayground((s) => s.groupBy)
    const setGroupBy = usePlayground((s) => s.setGroupBy)
    const direction = usePlayground((s) => s.direction)
    const setDirection = usePlayground((s) => s.setDirection)
    const availableGroupings = usePlayground((s) => s.availableGroupings)

    const [view, setView] = useState<'main' | 'layers' | 'groupby' | 'direction'>('main')

    // Reset to main view when the sheet closes.
    useEffect(() => { if (!open) setView('main') }, [open])

    const toggleFilter = (key: keyof StructureFilters) =>
        onFiltersChange({ ...filters, [key]: !filters[key] })

    const toggleAll = () => {
        onFiltersChange(Object.values(filters).every(Boolean) ? { ...ALL_OFF } : { ...ALL_ON })
    }

    const activeCount = Object.values(filters).filter(Boolean).length

    const content = (
        <>
            {view === 'main' && (
                <div className="flex flex-col">
                    <NavRow label="Layers" right={<Detail text={activeCount > 0 ? `${activeCount} on` : 'off'} />} onClick={() => setView('layers')} />
                    <NavRow label="Direction" right={<Detail text={direction === 'LR' ? 'left to right' : direction === 'TB' ? 'top to bottom' : 'auto'} />} onClick={() => setView('direction')} />
                    {availableGroupings.length > 0 && (
                        <NavRow label="Group by" right={<Detail text={groupBy ?? 'default'} />} onClick={() => setView('groupby')} />
                    )}
                </div>
            )}

            {view === 'layers' && (
                <div className="flex flex-col">

                    <CheckRow label="all" checked={Object.values(filters).every(Boolean)} onChange={toggleAll} />
                    {FILTER_KEYS.map(key => (
                        <CheckRow key={key} label={key} checked={filters[key]} onChange={() => toggleFilter(key)} />
                    ))}
                </div>
            )}

            {view === 'direction' && (
                <div className="flex flex-col">
                    <RadioRow label="auto" selected={!direction} onClick={() => setDirection(undefined)} />
                    <RadioRow label="left to right" selected={direction === 'LR'} onClick={() => setDirection('LR')} />
                    <RadioRow label="top to bottom" selected={direction === 'TB'} onClick={() => setDirection('TB')} />
                </div>
            )}

            {view === 'groupby' && (
                <div className="flex flex-col">

                    <RadioRow label="default" selected={!groupBy} onClick={() => setGroupBy(undefined)} />
                    {availableGroupings.map(v => (
                        <RadioRow key={v} label={v} selected={groupBy === v} onClick={() => setGroupBy(v)} />
                    ))}
                </div>
            )}
        </>
    )

    const handleReset = () => {
        if (!confirm('Reset layers and grouping to defaults?')) return
        onFiltersChange({ ...ALL_OFF })
        setGroupBy(undefined)
        setDirection(undefined)
        setView('main')
    }

    const title = view === 'main' ? 'Settings'
        : view === 'layers' ? 'Layers'
        : view === 'direction' ? 'Direction'
        : 'Group by'
    const onBack = view !== 'main' ? () => setView('main') : undefined
    return <MobileSheet open={open} onClose={onClose} onBack={onBack} onReset={handleReset} icon={Icons.settings} title={title}>{content}</MobileSheet>
}

/* ── Private sub-components ── */

function NavRow({ label, right, onClick }: { label: string; right?: React.ReactNode; onClick: () => void }) {
    return (
        <button onClick={onClick} className="flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-all active:scale-[0.97] hover:bg-white/5" style={{ color: 'var(--text)' }}>
            <span className="text-sm font-medium flex-1">{label}</span>
            {right}
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>›</span>
        </button>
    )
}

function Detail({ text }: { text: string }) {
    return <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{text}</span>
}

function RadioRow({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
    return (
        <button onClick={onClick} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all hover:bg-white/5" style={{ color: selected ? 'var(--accent)' : 'var(--text-muted)' }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--text-muted)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />}
            </span>
            <span className="text-xs font-bold font-mono tracking-tight">{label}</span>
        </button>
    )
}

function CheckRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer rounded-xl px-3 py-2.5 transition-all hover:bg-white/5" style={{ color: checked ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
            <input type="checkbox" checked={checked} onChange={onChange} className="accent-[var(--accent)]" style={{ width: 12, height: 12 }} />
            <span className="text-xs font-bold font-mono tracking-tight">{label}</span>
        </label>
    )
}

